import { useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { OUItem } from '@/api/types';
import { collectAncestorIdsOfMatches, collectMatchIds, rootsWithMatches, type OuNode } from './ouTree';
import { useDropdownPosition } from './useDropdownPosition';
import { useOuExpansion } from './useOuExpansion';
import { useOuSelection, type OuCheckState } from './useOuSelection';
import './OuTreeSelect.css';

/**
 * Bug (usabilidad, 2026-08-18) — con la granularidad del árbol restaurada (ver AdminRoles.tsx),
 * un catálogo real puede traer miles de nodos en 4 niveles, muchos con el MISMO nombre
 * ('department' se repite 51 veces — ver useOUs.ts). Sin buscador, encontrar una rama concreta
 * era desplazarse a mano por toda la jerarquía. Mismo patrón que ya usa `OUsSubTab` (la única
 * pantalla que veía el árbol completo antes de esto): `collectMatchIds` + `collectAncestorIdsOfMatches`
 * + `rootsWithMatches`, reutilizados tal cual. Oculto para árboles chicos (pocas raíces, sin ruido
 * real) para no meterle un campo de más a selectores que hoy funcionan bien con 6 ítems.
 */
const SEARCH_THRESHOLD = 8;

export interface OuTreeSelectProps {
  items: OUItem[];
  mode: 'single' | 'multi';
  value: string[];
  onChange: (ids: string[]) => void;
  variant?: 'inline' | 'dropdown';
  placeholder?: string;
  isDisabled?: boolean;
  /**
   * Nodos que no se pueden DESCUBRIR: si `value` los cubre, ninguna interacción del árbol puede
   * dejarlos fuera. No obliga a cubrirlos —un id bloqueado que no viene marcado se comporta como
   * cualquier otro—, así que el nombre es «no lo puedes quitar», no «tiene que estar».
   * Lo usa RF-081: el admin de OU no puede sacar su propio nodo del alcance de una campaña.
   */
  lockedIds?: readonly string[];
}

function findNodeLabel(nodes: OuNode[], nodeId: string): string | null {
  for (const node of nodes) {
    if (node.ou.id === nodeId) return node.ou.name;
    const childLabel = findNodeLabel(node.children, nodeId);
    if (childLabel) return childLabel;
  }
  return null;
}

export function OuTreeSelect({ items, mode, value, onChange, variant = 'inline', placeholder, isDisabled, lockedIds }: OuTreeSelectProps) {
  const { t } = useTranslation();
  const { tree, isCollapsed, toggle: toggleExpansion } = useOuExpansion(items);
  const { getState, toggle: toggleMultiSelection, isLocked } = useOuSelection(items, value, onChange, lockedIds);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverPosition = useDropdownPosition(triggerRef, dropdownOpen);

  const [search, setSearch] = useState('');
  const trimmedSearch = search.trim().toLowerCase();
  const matchIds = useMemo(
    () => (trimmedSearch ? collectMatchIds(tree, trimmedSearch) : null),
    [tree, trimmedSearch],
  );
  const forcedOpenIds = useMemo(
    () => (matchIds ? collectAncestorIdsOfMatches(tree, matchIds) : null),
    [tree, matchIds],
  );
  const visibleRoots = matchIds ? rootsWithMatches(tree, matchIds) : tree;
  const showSearch = items.length >= SEARCH_THRESHOLD;

  function effectiveIsCollapsed(nodeId: string): boolean {
    if (forcedOpenIds?.has(nodeId)) return false;
    return isCollapsed(nodeId);
  }

  function handleNodeInputChange(nodeId: string): void {
    if (isDisabled) return;
    if (mode === 'multi') {
      toggleMultiSelection(nodeId);
      return;
    }
    // En modo simple el único desmarcado posible es limpiar el valor actual; si está bloqueado, no.
    if (value[0] === nodeId && lockedIds?.includes(nodeId)) return;
    onChange(value[0] === nodeId ? [] : [nodeId]);
    if (variant === 'dropdown') setDropdownOpen(false);
  }

  function nodeCheckState(nodeId: string): OuCheckState {
    return mode === 'multi' ? getState(nodeId) : (value[0] === nodeId ? 'checked' : 'unchecked');
  }

  /**
   * Nodos inactivos SIN selección vigente: no aportan nada como fila propia (no se pueden elegir) y
   * antes ni siquiera existían en el catálogo — ver `AdminCampaigns.tsx`/`CampaignExceptions.tsx`
   * (bug 2026-08-21: una rama país inactiva ocultaba a sus hijas BU activas del catálogo plano y
   * `buildOuTree` las pintaba como raíces sueltas duplicadas). Restaurar la jerarquía trajo de vuelta
   * el nodo inactivo como fila propia, pero a pedido de usuario esa fila NO debe verse: se omite y
   * sus hijos se "injertan" un nivel arriba, bajo el ancestro activo más cercano, con el nombre de la
   * rama oculta añadido a su etiqueta para no perder la distinción entre hermanos con el mismo nombre
   * (p. ej. "Direccion General Grupo Sigma - LATAM-ECUADOR" vs "... - LATAM-PERU").
   * Si el nodo SÍ tiene selección vigente (checked/indeterminate) se sigue mostrando como antes —
   * ocultarlo dejaría sin forma de revisar o limpiar una selección heredada inválida.
   */
  function isHiddenInactiveParent(node: OuNode): boolean {
    return !node.ou.isActive && nodeCheckState(node.ou.id) === 'unchecked';
  }

  function renderNode(node: OuNode, depth: number, hiddenAncestorName?: string): ReactNode[] {
    if (isHiddenInactiveParent(node)) {
      return node.children.flatMap(childNode => renderNode(childNode, depth, node.ou.name));
    }

    const hasChildren = node.children.length > 0;
    const collapsed = hasChildren && effectiveIsCollapsed(node.ou.id);
    const checkState = nodeCheckState(node.ou.id);
    const nodeLocked = mode === 'multi'
      ? isLocked(node.ou.id)
      : Boolean(lockedIds?.includes(node.ou.id)) && value[0] === node.ou.id;
    // Nodos inactivos CON selección vigente: visibles pero no elegibles — ver `isHiddenInactiveParent`
    // para el resto de nodos inactivos, que ya no llegan aquí.
    const nodeInactive = !node.ou.isActive && checkState === 'unchecked';
    const displayLabel = hiddenAncestorName ? `${node.ou.name} - ${hiddenAncestorName}` : node.ou.name;

    return [(
      <li key={node.ou.id} className="ou-tree-select-item">
        <div className="ou-tree-select-row" style={{ paddingLeft: depth * 16 }}>
          {hasChildren ? (
            <button
              type="button"
              className={`ou-tree-select-chevron${collapsed ? '' : ' ou-tree-select-chevron--open'}`}
              onClick={() => toggleExpansion(node.ou.id)}
              aria-expanded={!collapsed}
              aria-label={collapsed ? t('admin.ouExpand') : t('admin.ouCollapse')}
            >
              <i className="fas fa-chevron-right" aria-hidden="true" />
            </button>
          ) : (
            <span className="ou-tree-select-spacer" />
          )}

          <label className={`ou-tree-select-label${nodeLocked ? ' ou-tree-select-label--locked' : ''}${nodeInactive ? ' ou-tree-select-label--inactive' : ''}`}>
            <input
              type={mode === 'multi' ? 'checkbox' : 'radio'}
              checked={checkState === 'checked'}
              ref={inputElement => {
                if (inputElement) inputElement.indeterminate = checkState === 'indeterminate';
              }}
              disabled={isDisabled || nodeLocked || nodeInactive}
              onChange={() => handleNodeInputChange(node.ou.id)}
              aria-label={displayLabel}
            />
            <span>{displayLabel}</span>
            {nodeLocked && (
              <i className="fas fa-lock ou-tree-select-lock" title={t('admin.ouLockedHint')} aria-label={t('admin.ouLockedHint')} />
            )}
            {nodeInactive && (
              <i className="fas fa-circle-minus ou-tree-select-inactive-icon" title={t('admin.ouInactiveHint')} aria-label={t('admin.ouInactiveHint')} />
            )}
          </label>
        </div>
        {hasChildren && !collapsed && (
          <ul className="ou-tree-select-children">
            {node.children.flatMap(childNode => renderNode(childNode, depth + 1))}
          </ul>
        )}
      </li>
    )];
  }

  const searchBox = showSearch && (
    <input
      type="search"
      className="ou-tree-select-search"
      value={search}
      onChange={(e) => setSearch(e.target.value)}
      placeholder={t('admin.ouSearchPlaceholder')}
      aria-label={t('admin.ouSearchPlaceholder')}
    />
  );

  const treeMarkup = matchIds && matchIds.size === 0
    ? <p className="ou-tree-select-empty">{t('admin.ouSearchNoResults')}</p>
    : <ul className="ou-tree-select-list">{visibleRoots.flatMap(rootNode => renderNode(rootNode, 0))}</ul>;

  if (variant === 'inline') {
    return (
      <div className="ou-tree-select ou-tree-select--inline">
        {searchBox}
        <div className="ou-tree-select-scroll">{treeMarkup}</div>
      </div>
    );
  }

  const selectedLabel = value[0] ? (findNodeLabel(tree, value[0]) ?? value[0]) : (placeholder ?? t('admin.ouSelectPlaceholder'));

  // El popover se monta en un portal con posición fija: dentro del flujo normal
  // quedaba recortado por contenedores con `overflow: hidden` (p. ej. la tarjeta
  // del formulario de administración de usuarios).
  const popoverStyle = popoverPosition
    ? ({
        '--ou-popover-top': `${popoverPosition.top}px`,
        '--ou-popover-left': `${popoverPosition.left}px`,
        '--ou-popover-width': `${popoverPosition.width}px`,
        '--ou-popover-max-height': `${popoverPosition.maxHeight}px`,
      } as CSSProperties)
    : undefined;

  return (
    <div className="ou-tree-select ou-tree-select--dropdown">
      <button
        type="button"
        ref={triggerRef}
        className="ou-tree-select-trigger"
        onClick={() => setDropdownOpen(previousOpen => !previousOpen)}
        disabled={isDisabled}
        aria-expanded={dropdownOpen}
      >
        <span>{selectedLabel}</span>
        <i className={`fas fa-chevron-down${dropdownOpen ? ' ou-tree-select-trigger-icon--open' : ''}`} aria-hidden="true" />
      </button>
      {dropdownOpen && popoverPosition && createPortal(
        <>
          <div className="ou-tree-select-scrim" onClick={() => setDropdownOpen(false)} />
          <div className="ou-tree-select-popover" style={popoverStyle}>
            {searchBox}
            <div className="ou-tree-select-scroll">{treeMarkup}</div>
          </div>
        </>,
        document.body,
      )}
    </div>
  );
}
